import React, { useState, useEffect, useMemo } from 'react';
import { Search, Upload, Plus } from 'lucide-react';
import type { Engagement, SortField, SortDirection, UploadConflict } from '../types';
import {
  db,
  getAllEngagements,
  engagementExists,
  getEngagementBundle,
  type EngagementBundle,
  type SubGrouping,
} from '../db/database';
import { formatDate, generateFilename, exportToFile, readFileAsText } from '../utils/format';
import { ActionsMenu } from './ActionsMenu';
import { Button } from './Button';
import { EngagementModal } from './EngagementModal';
import { DeleteModal } from './DeleteModal';
import { UploadConflictModal } from './UploadConflictModal';
import type { TBAccount } from '../types';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Type-guard: returns true when the parsed JSON looks like a Deep Bundle.
 *
 * Deliberately lenient on `version`:
 *  - `version` is NOT required. A hand-edited or externally generated bundle
 *    that omits the version field but still has the three data keys is treated
 *    as a bundle — not silently downgraded to a metadata-only import.
 *  - Empty arrays for `accounts` or `subGroupings` pass the check.
 *    Array.isArray([]) === true, so an engagement with no accounts or no
 *    sub-groupings will NOT be rejected here.
 *
 * A legacy file is a bare Engagement object at the root (has `clientName`
 * directly at the top level, no `.engagement` sub-key). Those fall through
 * to the else branch in handleUpload and are imported as metadata only.
 */
function isEngagementBundle(parsed: unknown): parsed is EngagementBundle {
  if (typeof parsed !== 'object' || parsed === null) return false;
  const p = parsed as Record<string, unknown>;
  return (
    'engagement' in p &&
    typeof p.engagement === 'object' &&
    p.engagement !== null &&
    'accounts' in p &&
    Array.isArray(p.accounts) &&
    'subGroupings' in p &&
    Array.isArray(p.subGroupings)
  );
}

/**
 * Validates that an Engagement object has the minimum required fields.
 * Returns a human-readable error string, or null if valid.
 */
function validateEngagementStructure(e: Partial<Engagement>): string | null {
  if (!e.clientName) return 'Missing required field: "clientName"';
  if (!e.taxYearEnd) return 'Missing required field: "taxYearEnd"';
  if (!e.taxForm)    return 'Missing required field: "taxForm"';
  return null;
}

// ============================================================================
// COMPONENT
// ============================================================================

interface EngagementTableProps {
  onOpenEngagement: (engagement: Engagement) => void;
}

export const EngagementTable: React.FC<EngagementTableProps> = ({
  onOpenEngagement,
}) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [sortField, setSortField] = useState<SortField>('clientName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedEngagement, setSelectedEngagement] =
    useState<Engagement | null>(null);
  const [modalMode, setModalMode] = useState<'new' | 'edit' | 'duplicate' | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<Engagement | null>(null);
  const [uploadConflict, setUploadConflict] = useState<UploadConflict | null>(
    null
  );

  /**
   * pendingBundle holds the full Deep Bundle while the user resolves an upload
   * conflict (overwrite vs. duplicate) or finishes a duplicate via the
   * EngagementModal. Always cleared once the import is committed or cancelled.
   *
   * null means the pending upload was a legacy metadata-only file — no child
   * records need to be written.
   */
  const [pendingBundle, setPendingBundle] = useState<EngagementBundle | null>(
    null
  );

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    loadEngagements();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      setIsSearching(true);
      const timer = setTimeout(() => setIsSearching(false), 300);
      return () => clearTimeout(timer);
    } else {
      setIsSearching(false);
    }
  }, [searchQuery]);

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadEngagements = async () => {
    const data = await getAllEngagements();
    setEngagements(data);
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const filteredEngagements = useMemo(() => {
    const filtered = engagements.filter((e) =>
      e.clientName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal: string, bVal: string;
      if (sortField === 'clientName') {
        aVal = a.clientName.toLowerCase();
        bVal = b.clientName.toLowerCase();
      } else if (sortField === 'taxYearEnd') {
        aVal = a.taxYearEnd;
        bVal = b.taxYearEnd;
      } else {
        aVal = a.taxForm;
        bVal = b.taxForm;
      }
      return sortDirection === 'asc'
        ? aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        : aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }, [engagements, searchQuery, sortField, sortDirection]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ── Save (new / edit / duplicate from EngagementModal) ────────────────────
  /**
   * When mode === 'duplicate' AND a pendingBundle is waiting, we run a single
   * Dexie transaction to atomically write the engagement + all child records,
   * re-keyed under the new UUID that the modal already baked into `engagement`.
   * All account and subGrouping IDs are replaced with fresh UUIDs so there is
   * never a primary-key collision with the source engagement's records.
   */
  const handleSaveEngagement = async (engagement: Engagement) => {
    if (modalMode === 'edit') {
      await db.engagements.put(engagement);
    } else if (modalMode === 'duplicate' && pendingBundle) {
      const newEngagementId = engagement.id!;

      const remappedAccounts: TBAccount[] = pendingBundle.accounts.map(
        (acc) => ({ ...acc, id: crypto.randomUUID(), engagementId: newEngagementId })
      );
      const remappedSubGroupings: SubGrouping[] = pendingBundle.subGroupings.map(
        (sg) => ({ ...sg, id: crypto.randomUUID(), engagementId: newEngagementId })
      );

      await db.transaction(
        'rw',
        [db.engagements, db.accounts, db.subGroupings],
        async () => {
          await db.engagements.add(engagement);
          if (remappedAccounts.length > 0)
            await db.accounts.bulkAdd(remappedAccounts);
          if (remappedSubGroupings.length > 0)
            await db.subGroupings.bulkAdd(remappedSubGroupings);
        }
      );

      setPendingBundle(null);
    } else {
      await db.engagements.add(engagement);
    }

    await loadEngagements();
    setModalMode(null);
    setSelectedEngagement(null);
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (engagement: Engagement) => {
    if (engagement.id) {
      await db.engagements.delete(engagement.id);
      await loadEngagements();
    }
    setDeleteTarget(null);
  };

  // ── Download (export to .TAXTRAIL) ─────────────────────────────────────────
  /**
   * Builds a Deep Bundle (engagement + accounts + subGroupings) and writes it
   * to a .TAXTRAIL file. Integer-cent values survive JSON.stringify / parse
   * unchanged because JavaScript's JSON codec handles integers losslessly.
   */
  const handleDownload = async (engagement: Engagement) => {
    try {
      const bundle = await getEngagementBundle(engagement.id!);
      const filename = generateFilename(
        engagement.clientName,
        engagement.taxYearEnd,
        engagement.taxForm
      );
      exportToFile(bundle, filename);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting engagement. Please try again.');
    }
  };

  // ── Upload (import from .TAXTRAIL) ─────────────────────────────────────────
  /**
   * Three-tier error handling — each failure mode produces a distinct,
   * actionable message instead of the old single "please ensure valid JSON":
   *
   *  Tier 1 — JSON Syntax Error
   *    JSON.parse threw a SyntaxError. The bytes on disk are not valid JSON.
   *    Message tells the user the file is malformed and shows the parser detail.
   *
   *  Tier 2 — Structure Error
   *    JSON parsed fine but required engagement fields (clientName / taxYearEnd
   *    / taxForm) are absent. Validated by validateEngagementStructure().
   *    Message names the specific missing field.
   *
   *  Tier 3 — Database Error
   *    Parse and validation both passed but the IndexedDB write failed.
   *    Primary cause: Dexie bulkAdd() throws a ConstraintError when account IDs
   *    from a prior partial import are still in the DB. Fixed by switching to
   *    bulkPut() (upsert) for child records on the no-conflict path, so orphaned
   *    records are silently overwritten rather than colliding.
   *    The Tier 3 catch exists as a backstop for any remaining DB-level failure
   *    and surfaces it with an accurate "saved to database" message — never the
   *    misleading "ensure the file is valid JSON" wording.
   *
   * Backward compatibility:
   *    If the file is valid JSON but isEngagementBundle() returns false (i.e.
   *    it lacks the .engagement / .accounts / .subGroupings keys), it is treated
   *    as a legacy metadata-only file and imported without crashing. The `version`
   *    key is not required — a bundle without it is still imported as a bundle.
   */
  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.TAXTRAIL,.json';

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // ── Tier 1: Read & parse ─────────────────────────────────────────────
      let parsed: unknown;
      try {
        const text = await readFileAsText(file);
        parsed = JSON.parse(text);
      } catch (err) {
        if (err instanceof SyntaxError) {
          alert(
            `Invalid .TAXTRAIL file: the file contains malformed JSON and could not be read.\n\nParser detail: ${err.message}`
          );
        } else {
          alert(
            'Could not read the selected file. Please ensure it is accessible and try again.'
          );
        }
        return;
      }

      // ── Detect format & extract the Engagement object ────────────────────
      let bundle: EngagementBundle | null = null;
      let uploadedEngagement: Engagement;

      if (isEngagementBundle(parsed)) {
        // New Deep Bundle format (version key is optional)
        bundle = parsed as EngagementBundle;
        uploadedEngagement = bundle.engagement;
      } else {
        // Legacy metadata-only format — bare Engagement object at the root
        uploadedEngagement = parsed as Engagement;
      }

      // ── Tier 2: Structural validation ───────────────────────────────────
      const structureError = validateEngagementStructure(uploadedEngagement);
      if (structureError) {
        alert(
          `Invalid .TAXTRAIL file: the JSON is valid but is missing required data.\n\n${structureError}`
        );
        return;
      }

      // ── Tier 3: Database operations ──────────────────────────────────────
      try {
        // Check for ID conflict before writing anything
        if (
          uploadedEngagement.id &&
          (await engagementExists(uploadedEngagement.id))
        ) {
          const existing = await db.engagements.get(uploadedEngagement.id);
          if (existing) {
            setPendingBundle(bundle);
            setUploadConflict({ existing, uploaded: uploadedEngagement });
            return;
          }
        }

        // No conflict — write atomically
        const newId = uploadedEngagement.id || crypto.randomUUID();
        const engagementToWrite: Engagement = { ...uploadedEngagement, id: newId };

        if (bundle) {
          const accounts: TBAccount[] = bundle.accounts.map((acc) => ({
            ...acc,
            engagementId: newId,
          }));
          const subGroupings: SubGrouping[] = bundle.subGroupings.map((sg) => ({
            ...sg,
            engagementId: newId,
          }));

          await db.transaction(
            'rw',
            [db.engagements, db.accounts, db.subGroupings],
            async () => {
              // add() for the engagement — confirmed non-existent above
              await db.engagements.add(engagementToWrite);
              // bulkPut() = upsert for child records. This silently overwrites
              // any orphaned records that share the same primary key (e.g. from
              // a previous partial import) instead of throwing a ConstraintError
              // that would have previously appeared as a false "invalid JSON" alert.
              if (accounts.length > 0) await db.accounts.bulkPut(accounts);
              if (subGroupings.length > 0)
                await db.subGroupings.bulkPut(subGroupings);
            }
          );
        } else {
          // Legacy file — metadata only, no child records to write
          await db.engagements.add(engagementToWrite);
        }

        await loadEngagements();
      } catch (dbError) {
        // The file was valid — the problem is in the database write
        console.error('Database write error during import:', dbError);
        alert(
          'The .TAXTRAIL file was read and validated successfully, but an error ' +
          'occurred while saving to the local database.\n\n' +
          'This is uncommon. Try uploading the file again. If the problem persists, ' +
          'try refreshing the page and uploading once more.'
        );
      }
    };

    input.click();
  };

  // ── Overwrite conflict resolution ──────────────────────────────────────────
  /**
   * Replaces the existing engagement and ALL child records with the bundle from
   * the uploaded file in a single atomic transaction. Uses bulkPut() (not
   * bulkAdd) so the write is idempotent even if records already exist.
   */
  const handleUploadOverwrite = async () => {
    if (!uploadConflict) return;

    const { uploaded: uploadedEngagement } = uploadConflict;
    const engagementId = uploadedEngagement.id!;

    try {
      if (pendingBundle) {
        await db.transaction(
          'rw',
          [db.engagements, db.accounts, db.subGroupings],
          async () => {
            // Replace engagement
            await db.engagements.put(uploadedEngagement);

            // Purge ALL existing child records for this engagement first so
            // records deleted in the new bundle don't linger in the DB
            await db.accounts
              .where('engagementId')
              .equals(engagementId)
              .delete();
            await db.subGroupings
              .where('engagementId')
              .equals(engagementId)
              .delete();

            // Write incoming child records
            const accounts: TBAccount[] = pendingBundle.accounts.map((acc) => ({
              ...acc,
              engagementId,
            }));
            const subGroupings: SubGrouping[] = pendingBundle.subGroupings.map(
              (sg) => ({ ...sg, engagementId })
            );

            if (accounts.length > 0) await db.accounts.bulkPut(accounts);
            if (subGroupings.length > 0)
              await db.subGroupings.bulkPut(subGroupings);
          }
        );
      } else {
        // Legacy file — replace engagement metadata only
        await db.engagements.put(uploadedEngagement);
      }

      await loadEngagements();
    } catch (error) {
      console.error('Overwrite error:', error);
      alert('Error overwriting engagement. No data was changed.');
    } finally {
      setPendingBundle(null);
      setUploadConflict(null);
    }
  };

  // ── Duplicate conflict resolution ──────────────────────────────────────────
  /**
   * Opens EngagementModal in 'duplicate' mode. pendingBundle stays in state
   * and is consumed by handleSaveEngagement once the user confirms.
   */
  const handleUploadDuplicate = async () => {
    if (!uploadConflict) return;

    setSelectedEngagement(uploadConflict.uploaded);
    setModalMode('duplicate');
    setUploadConflict(null);
    // pendingBundle intentionally left in state — consumed by handleSaveEngagement
  };

  // ── Cancel conflict modal ──────────────────────────────────────────────────
  const handleUploadConflictCancel = () => {
    setPendingBundle(null);
    setUploadConflict(null);
  };

  // ── Row interaction ────────────────────────────────────────────────────────
  const handleRowClick = (engagement: Engagement) => {
    setSelectedEngagement(engagement);
  };

  const handleRowDoubleClick = (engagement: Engagement) => {
    onOpenEngagement(engagement);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen">
      {/* Control Bar */}
      <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-700 z-20 transition-colors duration-300">
        <div className="p-4">
          <h1 className="text-2xl font-bold mb-4 text-taxtrail-text dark:text-taxtrail-dark-text">
            Engagement Library
          </h1>
          <div className="flex gap-3 items-center">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search engagements..."
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 bg-white dark:bg-neutral-800 text-taxtrail-text dark:text-taxtrail-dark-text placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-gray-300 dark:border-neutral-600 border-t-taxtrail-primary dark:border-t-green-500 rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Upload Button */}
            <Button
              variant="secondary"
              size="lg"
              leftIcon={<Upload className="w-5 h-5" />}
              onClick={handleUpload}
            >
              UPLOAD TAXTRAIL
            </Button>

            {/* New Engagement Button */}
            <Button
              variant="primary"
              size="lg"
              leftIcon={<Plus className="w-5 h-5" />}
              onClick={() => {
                setSelectedEngagement(null);
                setModalMode('new');
              }}
            >
              NEW ENGAGEMENT
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container with Padding */}
      <div className="flex-1 overflow-hidden p-8">
        <div
          className="h-full overflow-auto border border-gray-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-transparent transition-colors duration-300"
          style={{ scrollbarGutter: 'stable' }}
        >
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700 transition-colors duration-300">
              <tr>
                <th
                  className="text-left px-6 py-4 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors select-none text-taxtrail-text dark:text-taxtrail-dark-text"
                  onClick={() => handleSort('clientName')}
                >
                  <div className="flex items-center gap-2">
                    CLIENT NAME
                    {sortField === 'clientName' && (
                      <span className="text-xs">
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left px-6 py-4 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors select-none text-taxtrail-text dark:text-taxtrail-dark-text"
                  onClick={() => handleSort('taxYearEnd')}
                >
                  <div className="flex items-center gap-2">
                    TAX YEAR-END
                    {sortField === 'taxYearEnd' && (
                      <span className="text-xs">
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left px-6 py-4 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors select-none text-taxtrail-text dark:text-taxtrail-dark-text w-32"
                  onClick={() => handleSort('taxForm')}
                >
                  <div className="flex items-center gap-2">
                    TAX FORM
                    {sortField === 'taxForm' && (
                      <span className="text-xs">
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="text-left px-6 py-4 font-semibold text-taxtrail-text dark:text-taxtrail-dark-text">
                  DESCRIPTION
                </th>
                <th className="text-left px-6 py-4 font-semibold w-20 text-taxtrail-text dark:text-taxtrail-dark-text">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEngagements.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-20 text-gray-500 dark:text-gray-400"
                  >
                    {searchQuery ? (
                      'No engagements found'
                    ) : (
                      <div>
                        <p className="text-lg mb-2">
                          Welcome, create new engagement or upload existing
                          engagement to get started
                        </p>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredEngagements.map((engagement) => (
                  <tr
                    key={engagement.id}
                    className={`border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/50 cursor-pointer transition-colors ${
                      selectedEngagement?.id === engagement.id
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : ''
                    }`}
                    onClick={() => handleRowClick(engagement)}
                    onDoubleClick={() => handleRowDoubleClick(engagement)}
                  >
                    <td className="px-6 py-4 font-medium text-taxtrail-text dark:text-taxtrail-dark-text">
                      {engagement.clientName}
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-taxtrail-text dark:text-taxtrail-dark-text">
                      {formatDate(engagement.taxYearEnd)}
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-taxtrail-text dark:text-taxtrail-dark-text w-32">
                      {engagement.taxForm}
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                      {engagement.description || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <ActionsMenu
                        engagement={engagement}
                        onOpen={() => handleRowDoubleClick(engagement)}
                        onEdit={() => {
                          setSelectedEngagement(engagement);
                          setModalMode('edit');
                        }}
                        onDuplicate={() => {
                          setSelectedEngagement(engagement);
                          setModalMode('duplicate');
                        }}
                        onDownload={() => handleDownload(engagement)}
                        onDelete={() => setDeleteTarget(engagement)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {modalMode && (
        <EngagementModal
          mode={modalMode}
          engagement={selectedEngagement || undefined}
          onClose={() => {
            setPendingBundle(null); // discard pending bundle on cancel
            setModalMode(null);
            setSelectedEngagement(null);
          }}
          onSave={handleSaveEngagement}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          engagement={deleteTarget}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {uploadConflict && (
        <UploadConflictModal
          engagement={uploadConflict.uploaded}
          onOverwrite={handleUploadOverwrite}
          onDuplicate={handleUploadDuplicate}
          onCancel={handleUploadConflictCancel}
        />
      )}
    </div>
  );
};
